import { BlockStack, Box, Card, InlineStack, Text } from "@shopify/polaris";

import { money } from "../../utils/money";

import { t } from "../../lib/i18n";
import type { TaxGroup } from "../../api/purchase_orders";

type Props = {
  /** 税率ごとの小計・税額（その発注書で実際に使われた税率だけ）。 */
  taxGroups: TaxGroup[];
  totalAmount: string;
  /** 店の通貨（発注書ドラフトの `currency`）。 */
  currency: string;
};

/**
 * 右レールの「費用サマリー」。税区分別の集計が差別化なので、純正の単純税表示には
 * 退化させず**税率ごとの明細のまま**保つ。
 *
 * 🔴 税率名はマーチャントが設定した文字列なので `t()` に通さない
 *   （"標準 10%" / "VAT 20%" / "Reverse charge 0%"）。
 */
export function CostSummaryCard({ taxGroups, totalAmount, currency }: Props) {
  const yen = (amount: string) => money(amount, currency);
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingSm">
          {t("費用サマリー")}
        </Text>
        {taxGroups.map((group) => (
          <BlockStack gap="050" key={`${group.tax_rate_id ?? "none"}-${group.name}`}>
            <InlineStack align="space-between">
              <Text as="span" variant="bodySm" tone="subdued">
                {group.name}
              </Text>
              <Text as="span" variant="bodySm">
                {yen(group.subtotal)}
              </Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text as="span" variant="bodySm" tone="subdued">
                {t("うち税")}
              </Text>
              <Text as="span" variant="bodySm">
                {yen(group.tax)}
              </Text>
            </InlineStack>
          </BlockStack>
        ))}
        <Box borderBlockStartWidth="025" borderColor="border" paddingBlockStart="200">
          <InlineStack align="space-between">
            <Text as="span" variant="bodyMd" fontWeight="semibold">
              {/* 税率行が無い店（US 等）は税を計算しない＝「税込」と書くと嘘になる */}
              {taxGroups.length > 0 ? t("合計（税込）") : t("合計")}
            </Text>
            <Text as="span" variant="bodyMd" fontWeight="semibold">
              {yen(totalAmount)}
            </Text>
          </InlineStack>
        </Box>
      </BlockStack>
    </Card>
  );
}
